// BR builder
import {
  weekendMarkCaculate,
  isSmallScreenMobile,
} from '@/pages-v2/ShopDetail/components/DiagnosisCommon/utils/utils.js'
import { getBDLocale } from '@/common/utils/i18n'
import { designPxToRem } from './designPxToRem.js'

export function LegendBuilder(data = [], options = {}) {
  return {
    bottom: 0,
    height: 8,
    itemWidth: 8,
    itemHeight: 8,
    textStyle: {
      color: '#666',
      fontSize: 12,
      lineHeight: 8,
      fontFamily: 'DiDiSans-Pro-Regular',
    },
    itemGap: 10,
    selectedMode: true,
    ...options,
    data,
  }
}

export function ToolTipBuilder(options = {}) {
  const OVERLAY_SERIES_NAME = '__stacked_bar_border_overlay__'
  const {
    isPromotionInvest = false,
    formulaExposureMarker = false,
    dashedLineSeriesNames = [],
  } = options
  const getValidParams = params => {
    return (params || []).filter(item => item?.seriesName !== OVERLAY_SERIES_NAME)
  }
  const getTooltipMarker = item => {
    const r = designPxToRem
    const color = item?.color || '#ccc'
    if (item?.seriesType === 'line') {
      if (isPromotionInvest || formulaExposureMarker) {
        return `<span style="display:inline-block;width:${r(20)};height:${r(
          20,
        )};border-radius:50%;background:${color};vertical-align:middle;margin-right:${r(
          8,
        )};flex-shrink:0;"></span>`
      }
      // 虚线折线 → 黑底白边空心圆 marker；实线折线 → 白色实心圆 marker
      if (dashedLineSeriesNames.includes(item?.seriesName)) {
        return `<span style="display:inline-block;width:${r(6)};height:${r(
          6,
        )};border-radius:50%;border:${r(
          2,
        )} solid #fff;background:#0c0c0d;vertical-align:middle;margin-right:${r(
          8,
        )};flex-shrink:0;"></span>`
      } else {
        return `<span style="display:inline-block;width:${r(10)};height:${r(10)};border-radius:${r(
          5,
        )};background:#fff;vertical-align:middle;margin-right:${r(8)};flex-shrink:0;"></span>`
      }
    } else if (item?.seriesType === 'bar') {
      return `<span style="display:inline-block;width:${r(10)};height:${r(10)};border-radius:${r(
        2,
      )};background:${color};vertical-align:middle;margin-right:${r(8)};flex-shrink:0;"></span>`
    }
  }

  const getTooltipDate = params => {
    const validParams = getValidParams(params)
    const currentItem = validParams[0] || params?.[0] || {}
    return currentItem.axisValue || currentItem.name || ''
  }

  const clamp = (value, min, max) => {
    if (max < min) return min
    return Math.max(min, Math.min(value, max))
  }

  const getBandWidthByChart = ({ chart, dataIndex, categoryCount, xAxisIndex }) => {
    if (!chart || categoryCount <= 1 || typeof dataIndex !== 'number') return 0

    const prevIndex = dataIndex > 0 ? dataIndex - 1 : dataIndex
    const nextIndex = dataIndex < categoryCount - 1 ? dataIndex + 1 : dataIndex

    if (prevIndex === nextIndex) return 0

    const prevX = chart.convertToPixel({ xAxisIndex }, prevIndex)
    const nextX = chart.convertToPixel({ xAxisIndex }, nextIndex)
    const distance = Math.abs(nextX - prevX)

    if (!Number.isFinite(distance) || distance <= 0) return 0
    if (prevIndex !== dataIndex && nextIndex !== dataIndex) return distance / 2
    return distance
  }

  const { position: rawPosition, ...restOptions } = options
  const isStaticPositionObject =
    rawPosition &&
    typeof rawPosition === 'object' &&
    !Array.isArray(rawPosition) &&
    ['left', 'right', 'top', 'bottom'].some(key => rawPosition[key] !== undefined) &&
    ['alignOffset', 'barGap', 'bottomGap', 'bandRatio', 'fixedBandWidth'].every(
      key => rawPosition[key] === undefined,
    )

  const positionConfig =
    rawPosition &&
    typeof rawPosition === 'object' &&
    !Array.isArray(rawPosition) &&
    !isStaticPositionObject
      ? rawPosition
      : {}

  const getTooltipTop = ({ contentHeight, viewHeight, alignOffset, edgeGap }) => {
    const anchorBottom = Math.max(edgeGap, viewHeight - edgeGap)
    const top = anchorBottom - contentHeight + alignOffset
    return clamp(top, edgeGap, Math.max(edgeGap, viewHeight - contentHeight - edgeGap))
  }

  const getTooltipEdgeGap = () => {
    if (typeof positionConfig.edgeGap === 'number') {
      return positionConfig.edgeGap
    }
    return 8
  }

  const getTooltipBottomAnchorOffset = () => {
    if (typeof positionConfig.bottomAnchorOffset === 'number') {
      return positionConfig.bottomAnchorOffset
    }
    return -16
  }

  const tooltipOption = {
    show: true,
    enterable: false,
    trigger: 'axis',
    axisPointer: {
      type: 'none',
      animation: false,
    },
    backgroundColor: '#000',
    textStyle: {
      color: '#fff',
      fontSize: 12,
      fontFamily: 'DiDiSans-Pro-Regular',
      fontWeight: 400,
    },
    extraCssText: `padding: ${designPxToRem(24)}; border-radius: ${designPxToRem(
      16,
    )}; word-wrap: break-word; overflow-wrap:break-word; white-space:normal; text-align:left`,
    /**
     * 自定义 tooltip 定位函数
     *
     * 核心策略：tooltip 优先显示在柱子右侧，右侧放不下则翻转到左侧，
     * 垂直方向锚定在图表底部附近，最终做边界安全裁剪。
     *
     * @param {Array}  point  - 鼠标/触摸点的像素坐标 [x, y]
     * @param {Array}  params - ECharts 传入的当前触发点对应的系列数据数组
     * @param {HTMLElement} _dom  - tooltip 的 DOM 节点（未使用）
     * @param {Object} _rect - 触发区域的矩形信息（未使用）
     * @param {Object} size  - { contentSize: [w, h], viewSize: [w, h] }
     * @returns {Array} [left, top] tooltip 左上角的像素坐标
     */
    position: function (point, params, _dom, _rect, size) {
      // ========== 1. 读取配置参数 ==========
      // 边缘安全间距：tooltip 距离图表容器左右/上下边缘的最小像素距离
      const edgeGap = getTooltipEdgeGap()
      // 底部锚点偏移量：控制 tooltip 垂直方向相对于图表底部的偏移（负值表示向上偏移）
      const alignOffset = getTooltipBottomAnchorOffset()
      // tooltip 与柱子之间的水平间距（默认 8px）
      const barGap = typeof positionConfig.barGap === 'number' ? positionConfig.barGap : 8
      // 柱子宽度占 band（类目区间）宽度的比例（默认 0.56，即柱子占区间的 56%）
      const bandRatio =
        typeof positionConfig.bandRatio === 'number' ? positionConfig.bandRatio : 0.56

      // ========== 2. 解析基础尺寸信息 ==========
      // 鼠标/触摸点的 X 坐标
      const [pointX = 0] = point || []
      // tooltip 内容区域的宽高
      const [contentWidth = 0, contentHeight = 0] = size?.contentSize || []
      // 图表容器（viewBox）的宽高
      const [viewWidth = 0, viewHeight = 0] = size?.viewSize || []

      // ========== 3. 获取当前触发的数据项信息 ==========
      // 过滤掉辅助系列（如堆叠柱高亮边框覆盖层），取第一个有效数据项
      const validParams = getValidParams(params)
      const currentItem = validParams[0] || params?.[0] || {}
      // 当前触发的类目索引（第几根柱子）
      const dataIndex =
        typeof currentItem?.dataIndex === 'number'
          ? currentItem.dataIndex
          : Number(currentItem?.dataIndex)

      // ========== 4. 从图表元数据中获取坐标轴和实例信息 ==========
      // __chartMeta 由外部在 setOption 时注入，携带 xAxis 相关信息和图表实例引用
      const meta = tooltipOption.__chartMeta || {}
      const xAxisIndex = typeof meta.xAxisIndex === 'number' ? meta.xAxisIndex : 0
      const xAxisData = Array.isArray(meta.xAxisData) ? meta.xAxisData : []
      const categoryCount = xAxisData.length
      // 获取 ECharts 实例引用（用于 convertToPixel 坐标转换）
      const chart = typeof meta.getChartInstance === 'function' ? meta.getChartInstance() : null

      // ========== 5. 计算垂直位置（top） ==========
      // 垂直方向：锚定在图表底部附近，并做上下边界裁剪
      const top = getTooltipTop({ contentHeight, viewHeight, alignOffset, edgeGap })

      // ========== 6. 计算水平位置（left） ==========
      // 默认 fallback：tooltip 紧贴鼠标点右侧
      let left = pointX + barGap

      if (Number.isFinite(dataIndex) && categoryCount > 0) {
        // —— 有效的类目数据场景：基于柱子精确位置计算 ——

        // 6a. 确定 bandWidth（每个类目在 X 轴上占的像素宽度）
        //     优先级：外部固定值 > 通过图表实例计算 > 均分容器宽度 > 屏幕尺寸兜底
        const useFixedBand = typeof positionConfig.fixedBandWidth === 'number'
        let bandWidth = useFixedBand ? positionConfig.fixedBandWidth : 0

        if (!bandWidth && chart) {
          // 通过相邻两个类目点的像素距离计算实际 bandWidth
          bandWidth = getBandWidthByChart({ chart, dataIndex, categoryCount, xAxisIndex })
        }
        if (!bandWidth && categoryCount > 0) {
          // 均分容器宽度作为近似值
          bandWidth = viewWidth / categoryCount
        }
        if (!bandWidth) {
          // 最终兜底：小屏 36px，普通屏 44px
          bandWidth = isSmallScreenMobile() ? 36 : 44
        }

        // 6b. 确定当前柱子的中心 X 坐标
        //     优先通过 ECharts API 精确转换，失败时用 (index + 0.5) * bandWidth 估算
        let centerX = chart?.convertToPixel?.({ xAxisIndex }, dataIndex)
        if (!Number.isFinite(centerX)) {
          centerX = (dataIndex + 0.5) * bandWidth
        }

        // 6c. 估算柱子的左右边界
        //     halfBarRegion = 柱子半宽，由 bandWidth * bandRatio 决定，限制在 [8px, bandWidth/2] 之间
        const halfBarRegion = clamp(bandWidth * bandRatio * 0.5, 8, bandWidth / 2)
        const estimatedBarLeft = centerX - halfBarRegion
        const estimatedBarRight = centerX + halfBarRegion

        // 6d. 优先将 tooltip 放在柱子右侧
        left = estimatedBarRight + barGap
        if (left + contentWidth > viewWidth - edgeGap) {
          // 右侧放不下 → 翻转到柱子左侧
          left = estimatedBarLeft - contentWidth - barGap
        }
      } else if (left + contentWidth > viewWidth - edgeGap) {
        // —— 无有效类目数据的 fallback 场景（如折线图无柱子） ——
        // 右侧放不下 → 翻转到鼠标点左侧
        left = pointX - contentWidth - barGap
      }

      // ========== 7. 最终边界安全裁剪 ==========
      // 确保 tooltip 不超出容器左右边缘（保留 edgeGap 间距）
      left = clamp(left, edgeGap, Math.max(edgeGap, viewWidth - contentWidth - edgeGap))
      return [left, top]
    },
    formatter: function (params) {
      const r = designPxToRem
      const validParams = getValidParams(params)
      const date = getTooltipDate(validParams)
      const content = validParams
        .map((item, index) => {
          const marker = getTooltipMarker(item)
          const { seriesName, value } = item
          // 数据缺失时（null/undefined）显示占位符 '--'，避免 tooltip 中出现空白或 "null" 字样
          const displayValue = value === null || value === undefined ? '--' : value
          const marginBottom = index === validParams.length - 1 ? 0 : r(8)
          return `<div style="display:flex;align-items:center;line-height:${r(
            32,
          )};margin-bottom:${marginBottom};"><div style="display:flex;align-items:center;width:${r(
            192,
          )};font-family:DiDiSans-pro-Regular;flex-shrink:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;color:#89898c;">${marker}<span style="overflow:hidden;white-space:nowrap;text-overflow:ellipsis;color:#89898c;">${seriesName}</span></div><span style="flex:1;text-align:right;white-space:nowrap;font-weight:700;color:#fff;">${displayValue}</span></div>`
        })
        .join('')

      return `<div><div style="line-height:${r(32)};margin-bottom:${r(
        8,
      )};">${date}</div>${content}</div>`
    },
    ...restOptions,
  }

  if (typeof rawPosition === 'function' || isStaticPositionObject) {
    tooltipOption.position = rawPosition
  }

  return tooltipOption
}

export function XAxisBuilder(options = {}) {
  return {
    type: 'category',
    axisLabel: {
      fontSize: 10,
      fontFamily: 'PingFangSC-Regular',
      color: '#8D9099',
      padding: [2, 0, 0, 0],
    },
    axisTick: {
      show: false,
    },
    axisLine: {
      lineStyle: {
        color: '#cccccc',
      },
    },
    ...options,
  }
}

export function getAdaptiveBarWidthByCount(count = 0) {
  if (count <= 7) return 24
  if (count <= 20) return 8
  return 6
}

export function BarSelectedBorderBuilder(options = {}) {
  const { barWidth, ...rest } = options
  const borderWidth = barWidth ? Math.round((barWidth * 4) / 24) : 4
  return {
    itemStyle: {
      borderRadius: 4,
      barBorderRadius: 4,
    },
    emphasis: {
      itemStyle: {
        borderRadius: 4,
        barBorderRadius: 4,
        borderColor: '#B3D3FF',
        borderWidth,
        borderType: 'solid',
      },
    },
    ...rest,
  }
}

export function YAxisBuilder(options = {}) {
  return {
    type: 'value',
    axisTick: {
      show: false,
    },
    axisLine: {
      show: false,
    },
    splitLine: {
      show: true,
      lineStyle: {
        type: 'dotted',
        dashOffset: 10,
      },
    },
    nameTextStyle: {
      color: '#8D9099',
      fontSize: 10,
    },
    axisLabel: {
      fontSize: 10,
      fontFamily: 'PingFangSC-Regular',
      color: '#8D9099',
    },
    ...options,
  }
}

export function WeekendBuilder(dateDate = [], dateToWeek = {}) {
  return {
    type: 'custom',
    zIndex: 0,
    silent: true,
    markArea: weekendMarkCaculate(dateDate, dateToWeek),
  }
}
// 为了解决翻译长度问题，需要调整图标的高度和距离底部的位置
export function getEchartHeightAndBottomByLocale(category) {
  const locale = getBDLocale()
  const fixedChartHeight = 180
  const fixedChartHeightHor = 240
  const FIXEDCHARTBOTTOM = 24
  // br 配置 - 语言（葡语or英语）优化见下方localeConfig
  const BrazilCfg = {
    completeOrder: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    promotionInvest: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    promotionPromo: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    users: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    }, // 用户通用
    usersAllUser: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    }, // 用户 - 全部用户
    usersPercentage: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    }, // 用户 - 比例
    serviceDuration: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    serviceTimeout: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    serviceCancel: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    serviceQuestion: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
    // online: { height: fixedChartHeight, bottom: 56, heightHor: 200, bottomHor: 56 }, // 在线模块已作废
    usersAndConversion: {
      height: fixedChartHeight,
      bottom: FIXEDCHARTBOTTOM,
      heightHor: fixedChartHeightHor,
      bottomHor: FIXEDCHARTBOTTOM,
    },
  }
  const localeConfig = {
    'zh-CN': {
      ...BrazilCfg,
      promotionPromo: {
        // 投资&营销
        height: fixedChartHeight,
        bottom: FIXEDCHARTBOTTOM,
        heightHor: fixedChartHeightHor,
        bottomHor: FIXEDCHARTBOTTOM,
      },
    },
    'es-MX': {
      completeOrder: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
      promotion: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
      users: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
      serviceCancle: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
      serviceQuestion: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
      online: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
      usersAndConversion: { height: 180, bottom: 24, heightHor: 240, bottomHor: 24 },
    },
    'en-US': {
      ...BrazilCfg,
      // 英语优化：
      promotionPromo: {
        // 投资&营销
        height: fixedChartHeight,
        bottom: FIXEDCHARTBOTTOM,
        heightHor: fixedChartHeightHor,
        bottomHor: FIXEDCHARTBOTTOM,
      },
    },
    'pt-BR': {
      ...BrazilCfg,
      // 葡语优化：
      promotionPromo: {
        // 投资&营销
        height: fixedChartHeight,
        bottom: FIXEDCHARTBOTTOM,
        heightHor: fixedChartHeightHor,
        bottomHor: FIXEDCHARTBOTTOM,
      },
    },
  }
  return (localeConfig[locale] && localeConfig[locale][category]) || localeConfig['pt-BR'][category]
}

export const chartKeyForHorShow = ['promotion', 'service', 'users']

export const normalRoleChartKeyForHorShow = ['promotion', 'service', 'users']
